import React, { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import compact from 'lodash/compact';
import { PageContentHeader, TableListProvider, TableListHeader, FilterPickerGroup, Searchbox, WithStatusIcon, useMediaQueries, MultiSelectFilterPicker, SingleSelectFilterPicker } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { useIoContext, useStore, useStoreActions } from "../../contexts";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useDebouncedLogSearchToBI } from "../../hooks/useDebouncedLogSearchToBI";
import { useOnMount } from "../../hooks/useOnMount";
import { selectors } from "../../store/state-selectors";
import { useFullPageInvoiceOnRefresh } from "../FullPageInvoice/FullPageInvoiceOpener";
import { NoFilteredInvoices } from "../InvoicesEmptyStates/InvoicesEmptyStates";
import { DateFilterPicker } from "./DateFilterPicker";
import { getInvoiceStatusFilterOptions, getPaymentStatusFilterOptions, InvoiceHeaderItemID, applySort, QboStatusFilter, applyFilters, getQboStatusFilterOptions, applyOptimisticUpdate } from "./InvoicesTable.utils";
import InvoicesTableRow from "./InvoicesTableRow";
import { getURLWithFullPageInvoice } from "../FullPageInvoice/FullPageInvoice.utils";
import { invoiceSummaryModel } from "../../models";
export const defaultDateFilter = 'all-time';
const InvoicesTable = /*#__PURE__*/React.forwardRef(function InvoicesTable(props, ref) {
  const {
    filtersClassname,
    scrollEl,
    retry
  } = props;
  const [invoicesFromServer, setInvoicesFromServer] = useState(props.invoices);
  useEffect(() => setInvoicesFromServer(props.invoices), [props.invoices]);
  const {
    getNow
  } = useIoContext();
  const isVendor = useStore(selectors.user.isVendor);
  const {
    logBI,
    fetchIntegrations
  } = useStoreActions();
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const isPendingQboIntegrationSetup = useStore(selectors.integration.isPendingQboIntegrationSetup);
  const {
    smallScreen
  } = useMediaQueries();
  const {
    registerOnRefresh,
    unregisterOnRefresh
  } = useFullPageInvoiceOnRefresh();
  const hideDueDate = smallScreen;
  const defaultSort = useMemo(() => ({
    colIndex: 0,
    sort: 'desc'
  }), []);
  const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11451);
  const {
    urlState: {
      status: statusFilter,
      paymentStatus: paymentStatusFilter,
      date: dateFilter,
      search,
      qbo: qboStatusFilter
    },
    changeUrlState
  } = useStateFromUrl({
    status: {
      multi: true
    },
    paymentStatus: {
      multi: true
    },
    date: {
      defaultValue: defaultDateFilter
    },
    search: {
      defaultValue: ''
    },
    qbo: {
      defaultValue: QboStatusFilter.All
    }
  });
  const invoiceStatusFilterOptions = useMemo(() => {
    return getInvoiceStatusFilterOptions(applyFilters({
      fullInvoices: invoicesFromServer,
      statusFilter: null,
      paymentStatusFilter,
      dateFilter,
      qboStatusFilter,
      search,
      isVendor,
      now: getNow()
    }));
  }, [invoicesFromServer, isVendor, paymentStatusFilter, dateFilter, qboStatusFilter, search]);
  const paymentStatusFilterOptions = useMemo(() => {
    return getPaymentStatusFilterOptions(applyFilters({
      fullInvoices: invoicesFromServer,
      statusFilter,
      paymentStatusFilter: null,
      dateFilter,
      qboStatusFilter,
      search,
      isVendor,
      now: getNow()
    }), isVendor);
  }, [invoicesFromServer, statusFilter, isVendor, dateFilter, qboStatusFilter, search]);
  const qboStatusFilterOptions = useMemo(() => {
    return getQboStatusFilterOptions(applyFilters({
      fullInvoices: invoicesFromServer,
      statusFilter,
      paymentStatusFilter,
      dateFilter,
      qboStatusFilter: null,
      search,
      isVendor,
      now: getNow()
    }));
  }, [invoicesFromServer, statusFilter, paymentStatusFilter, dateFilter, search, isVendor]);
  const {
    columnDefs,
    headerItems,
    sortIds
  } = useMemo(() => {
    const invoiceID = 'Invoice ID';
    const tableDefs = compact([{
      headerItem: {
        id: InvoiceHeaderItemID.invoiceId,
        value: showQboIntegration && isPendingQboIntegrationSetup ? /*#__PURE__*/React.createElement(WithStatusIcon, {
          "data-testid": "missing-account-mapping-error",
          info: "Please finish setting up QBO by selecting the accounts in the integration setup page"
        }, invoiceID) : invoiceID
      },
      columnDef: {
        width: showQboIntegration ? 130 : 100,
        sortable: true
      }
    }, {
      headerItem: {
        id: InvoiceHeaderItemID.companyName,
        value: 'Company name'
      },
      columnDef: {
        width: [180, 400],
        sortable: true
      }
    }, {
      headerItem: {
        id: InvoiceHeaderItemID.status,
        value: 'Invoice status'
      },
      columnDef: {
        width: 96,
        sortable: true
      }
    }, {
      headerItem: {
        id: InvoiceHeaderItemID.additionalInformation,
        value: 'Payment status'
      },
      columnDef: {
        width: 220,
        sortable: true
      }
    }, {
      headerItem: {
        id: InvoiceHeaderItemID.issueDate,
        value: 'Issue Date'
      },
      columnDef: {
        width: [90, 100],
        sortable: true
      }
    }, !hideDueDate && {
      headerItem: {
        id: InvoiceHeaderItemID.dueDate,
        value: 'Due date'
      },
      columnDef: {
        width: [90, 100],
        sortable: true
      }
    }, {
      headerItem: {
        id: InvoiceHeaderItemID.total,
        value: 'Total'
      },
      columnDef: {
        width: 84,
        sortable: true,
        hAlign: 'end'
      }
    }]);
    const headerItems = tableDefs.map(d => d.headerItem);
    const columnDefs = tableDefs.map(d => d.columnDef);
    const sortIds = headerItems.map(item => item.id);
    return {
      columnDefs,
      headerItems,
      sortIds
    };
  }, [showQboIntegration, isPendingQboIntegrationSetup, hideDueDate]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  useOnMount(() => {
    if (showQboIntegration) {
      fetchIntegrations();
    }
    const retryId = 'retry';
    registerOnRefresh(retryId, async _ref => {
      let {
        optimisticUpdate
      } = _ref;
      if (optimisticUpdate) {
        setInvoicesFromServer(inv => applyOptimisticUpdate(inv, optimisticUpdate));
      } else {
        retry();
      }
    });
    return () => {
      unregisterOnRefresh(retryId);
    };
  });
  const filteredAndSortedInvoices = useMemo(() => {
    return applySort({
      invoices: applyFilters({
        fullInvoices: invoicesFromServer,
        statusFilter,
        paymentStatusFilter,
        dateFilter,
        qboStatusFilter,
        search,
        isVendor,
        now: getNow()
      }),
      isVendor,
      sort,
      headerItems
    });
  }, [search, dateFilter, invoicesFromServer, sort, isVendor, statusFilter, paymentStatusFilter, qboStatusFilter, headerItems]);
  useImperativeHandle(ref, () => ({
    getRows: () => filteredAndSortedInvoices
  }), [filteredAndSortedInvoices]);
  const {
    paginatedData,
    onScroll
  } = useInfiniteScroll({
    data: filteredAndSortedInvoices,
    scrollElRef: scrollEl
  });
  useEffect(() => {
    if (scrollEl) {
      const scrollHandler = e => onScroll(e);
      scrollEl.addEventListener('scroll', scrollHandler);
      return () => scrollEl.removeEventListener('scroll', scrollHandler);
    }
  }, [scrollEl, onScroll]);
  const resetFilters = () => {
    changeUrlState({
      date: defaultDateFilter,
      status: [],
      paymentStatus: [],
      search: ''
    });
  };
  const onSortChange = sort => {
    logBI(11450, {
      column: sort ? headerItems[sort.colIndex].id : 'none'
    });
    changeSort(sort);
  };
  return invoicesFromServer.length > 0 ? /*#__PURE__*/React.createElement(TableListProvider, {
    hasActionColumn: true,
    columnDefs: columnDefs,
    sort: sort,
    onSortChange: onSortChange
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    className: filtersClassname
  }, /*#__PURE__*/React.createElement(FilterPickerGroup, {
    "data-testid": "invoices-dashboard-filter-picker-group",
    onClearClick: resetFilters,
    disableClear: dateFilter === defaultDateFilter && statusFilter.length === 0 && paymentStatusFilter.length === 0 && !search
  }, /*#__PURE__*/React.createElement(Searchbox, {
    registerHotkey: true,
    value: search,
    "data-testid": "invoices-dashboard-search",
    onChange: event => {
      const searchStr = event.target.value;
      changeUrlState({
        search: searchStr
      });
      debouncedLogSearchToBI.onChange(searchStr);
    },
    onClear: () => {
      changeUrlState({
        search: ''
      });
      debouncedLogSearchToBI.onClear();
    }
  }), /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
    "data-testid": "invoices-status-filter-picker",
    value: statusFilter,
    options: invoiceStatusFilterOptions,
    label: "Invoice status",
    onChange: status => {
      changeUrlState({
        status
      });
      if (status.length) {
        logBI(11452, {
          status
        });
      }
    }
  }), /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
    "data-testid": "invoices-payment-status-filter-picker",
    value: paymentStatusFilter,
    options: paymentStatusFilterOptions,
    label: "Payment status",
    onChange: paymentStatus => changeUrlState({
      paymentStatus
    })
  }), /*#__PURE__*/React.createElement(DateFilterPicker, {
    value: dateFilter,
    onChange: date => {
      changeUrlState({
        date
      });
      if (date !== defaultDateFilter) {
        logBI(11453, {
          date
        });
      }
    }
  }), showQboIntegration && qboStatusFilterOptions.length > 0 && /*#__PURE__*/React.createElement(SingleSelectFilterPicker, {
    "data-testid": "invoices-qbo-filter",
    label: "Sync status",
    value: qboStatusFilter,
    options: qboStatusFilterOptions,
    onChange: option => option && changeUrlState({
      qbo: option.value
    })
  }))), /*#__PURE__*/React.createElement(TableListHeader, {
    "data-testid": "invoices-table-header",
    items: headerItems
  })), filteredAndSortedInvoices.length > 0 ? paginatedData.map((invoice, index) => /*#__PURE__*/React.createElement(InvoicesTableRow, {
    key: invoice.id,
    invoice: invoice,
    index: index,
    hideDueDate: hideDueDate,
    showQboIntegration: showQboIntegration,
    to: getURLWithFullPageInvoice({
      invoiceId: invoice.id,
      relationshipId: invoiceSummaryModel.relationshipId(invoice)
    }),
    retry: retry
  })) : /*#__PURE__*/React.createElement(NoFilteredInvoices, {
    resetFilters: resetFilters
  })) : null;
});
export default InvoicesTable;